import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import PipelineFilter from "@via/pages/pipelines/pipeline-filter.tsx";
import { usePipelineList } from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { useAddPipelineToProject } from "@via/pages/projects/projects.api.tsx";
import { ModalSize } from "@via/utils/constants";
import { addPipelineToProjectOptions } from "@via/utils/form-validators";
import { PipelineInterface } from "@via/utils/interfaces";
import {
  PipelineViewType,
  type InsertPipelineIntoProjectRequestParams,
} from "common";
import { useCallback, useEffect, useState, type FC } from "react";
import { useForm } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";

interface AddPipelineModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  projectId: string;
  pipelineId: string | undefined;
}

const AddPipelineModal: FC<AddPipelineModalProps> = ({
  open,
  setOpen,
  projectId,
  pipelineId,
}) => {
  const [selectedPipelineType, setSelectedPipelineType] = useState<
    PipelineViewType[]
  >([PipelineViewType.Released]);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const includeAllVersions = true;
  const [searchedInput, setSearchedInput] = useState("");
  const [cumulativePipelines, setCumulativePipelines] = useState<
    PipelineInterface[]
  >([]);
  const { data: pipelines, isLoading: isPipelineLoading } = usePipelineList(
    page,
    pageSize,
    selectedPipelineType,
    searchedInput,
    "",
    includeAllVersions
  );

  const { mutateAsync: addPipeline, isPending: isPipelineAddLoading } =
    useAddPipelineToProject();

  const fetchMorePipelines = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((pipelines?.total ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [pipelines?.total]);

  useEffect(() => {
    setCumulativePipelines([]);
  }, [selectedPipelineType]);

  useEffect(() => {
    if (pipelines?.data) {
      setCumulativePipelines(prevPipelines => {
        // Append new results while preventing duplicates
        const existingIds = new Set(prevPipelines.map(p => p.id));
        const newPipelines = pipelines?.data?.filter(
          p => !existingIds.has(p.id)
        );

        return [...prevPipelines, ...newPipelines];
      });
    }
  }, [pipelines?.data, page, searchedInput, selectedPipelineType]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchedInput) return;
    setPage(0);
    setSearchedInput(value);
  }, 300);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InsertPipelineIntoProjectRequestParams>({
    mode: "onChange",
    defaultValues: {
      projectId: parseInt(projectId, 10),
      ...(pipelineId && { pipelineId: parseInt(pipelineId, 10) }),
    },
  });

  // Modal closes on true and stays open on false (invalid form) or a rejection (failed add,
  // which the hook reports). The request used to be fired and the dialog closed before it
  // answered, so a failed add closed the dialog too.
  const handleConfirmModal = async (): Promise<boolean> => {
    let added = false;
    await handleSubmit(
      async (payload: InsertPipelineIntoProjectRequestParams) => {
        await addPipeline(payload);
        added = true;
      }
    )();
    return added;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Add Pipeline"
      confirmText="Add Pipeline"
      isConfirmLoading={isPipelineAddLoading}
      size={ModalSize.Large}
      onConfirm={handleConfirmModal}>
      <form onSubmit={e => e.preventDefault()}>
        <>
          <PipelineFilter
            selectedPipelineType={selectedPipelineType}
            setSelectedPipelineType={setSelectedPipelineType}
            setPage={() => {}}
            setPageSize={() => {}}
          />
          <div className="my-5">
            <div className="mt-2">
              <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                Choose Pipeline <span className="text-red-400">*</span>
              </label>
              <div className="mb-4">
                <SelectComponent
                  control={control}
                  name="pipelineId"
                  rules={addPipelineToProjectOptions.pipelineId}
                  options={
                    cumulativePipelines?.map(p => ({
                      label: p.name,
                      value: p.id,
                      version: p.version,
                    })) ?? []
                  }
                  isLoading={isPipelineLoading}
                  placeholder="Choose a Pipeline"
                  onFetchMoreOptions={fetchMorePipelines}
                  onInputChange={inputValue =>
                    debouncedHandleInputChange(inputValue)
                  }
                  formatOptionLabel={option => (
                    <div>
                      {option.label}({option.version ?? ""})
                    </div>
                  )}
                />
                {errors.pipelineId && (
                  <span className="text-xs text-rose-500">
                    {errors.pipelineId.message}
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      </form>
    </Modal>
  );
};

export default AddPipelineModal;
